import { useMemo, useState, ReactNode } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/lib/api/stores';
import { usePaymentFlowWithProcessors } from '@/lib/api/paymentFlows';
import { Storefront } from '../types';

interface DryRunSandboxWarningProps {
  funnel: Storefront;
  storeId: string;
  children: ReactNode;
}

interface WarningProcessor {
  name: string;
  reasons: string[];
}

/**
 * Gate component: resolves the active paymentFlowId, checks processors for
 * sandbox/dry-run/test-mode. If any found, shows warning with "I Understand"
 * button before revealing children. If no issue, renders children directly.
 */
export function DryRunSandboxWarning({ funnel, storeId, children }: DryRunSandboxWarningProps) {
  const { t } = useTranslation('storefront');
  const [acknowledged, setAcknowledged] = useState(false);
  const { data: store, isLoading: isLoadingStore } = useStore(storeId);

  const paymentFlowId = useMemo(() => {
    const nodes = funnel.nodes ?? [];
    for (const node of nodes) {
      if (node.type === 'checkout') {
        const flowId =
          node.config?.stepConfig?.payment?.paymentFlowId ?? node.config?.paymentFlowId;
        if (flowId) return flowId;

        if (node.variants) {
          for (const variant of node.variants) {
            const variantFlowId =
              variant.config?.stepConfig?.payment?.paymentFlowId ?? variant.config?.paymentFlowId;
            if (variantFlowId) return variantFlowId;
          }
        }
      }
    }
    return store?.selectedPaymentFlowId ?? null;
  }, [funnel.nodes, store?.selectedPaymentFlowId]);

  const { data: flowData, isLoading: isLoadingFlow } = usePaymentFlowWithProcessors(paymentFlowId ?? '', !!paymentFlowId);

  const warningProcessors = useMemo<WarningProcessor[]>(() => {
    if (!flowData) return [];

    const allConfigs = [
      ...(flowData.processorConfigs ?? []),
      ...(flowData.fallbackProcessorConfigs ?? []),
    ];

    const result: WarningProcessor[] = [];

    for (const config of allConfigs) {
      const proc = config.processor;
      if (!proc) continue;

      const options = proc.options as Record<string, unknown> | null;
      const reasons: string[] = [];

      if (proc.type === 'sandbox') reasons.push('sandbox');
      if (options?.dryMode === true) reasons.push('dry-run');
      if (options?.testMode === true) reasons.push('test-mode');

      if (reasons.length > 0) {
        if (!result.some((r) => r.name === proc.name)) {
          result.push({ name: proc.name, reasons });
        }
      }
    }

    return result;
  }, [flowData]);

  const isLoading = isLoadingStore || isLoadingFlow;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // No warning processors → pass through
  if (warningProcessors.length === 0 || acknowledged) {
    return <>{children}</>;
  }

  return (
    <div className="space-y-4 py-4">
      <Alert className="border-orange-300 bg-orange-50 dark:border-orange-700 dark:bg-orange-900/20">
        <AlertTriangle className="h-5 w-5 text-orange-600" />
        <AlertTitle className="text-sm font-semibold text-orange-800 dark:text-orange-200">
          {t('promote.dryRunWarningTitle')}
        </AlertTitle>
        <AlertDescription className="mt-2 space-y-2 text-xs leading-relaxed text-orange-700 dark:text-orange-300">
          <p>
            {t('promote.dryRunWarningDescription')}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {warningProcessors.map((p) => (
              <Badge
                key={p.name}
                variant="outline"
                className="border-orange-400 text-[10px] text-orange-700 dark:border-orange-600 dark:text-orange-300"
              >
                {p.name} &mdash; {p.reasons.join(', ')}
              </Badge>
            ))}
          </div>
        </AlertDescription>
      </Alert>
      <Button
        variant="outline"
        className="w-full border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-300 dark:hover:bg-orange-900/20"
        onClick={() => setAcknowledged(true)}
      >
        {t('promote.dryRunWarningAcknowledge')}
      </Button>
    </div>
  );
}
